import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import DocumentItem from '@/components/documents/DocumentItem';
import DocumentPreviewModal from '@/components/documents/DocumentPreviewModal';
import { useDocumentManagement } from '@/hooks/useDocumentManagement';
import CaseCriteriaAnalysis from '@/components/case/CaseCriteriaAnalysis';
import { DocumentWithStatus } from '@/types/document';
import {
  RiUploadLine,
  RiDownloadCloud2Line,
  RiLoader4Line,
  RiFileTextLine,
} from 'react-icons/ri';
// Pagination removed for folder tree mode
import FolderTreeEnhanced from '@/components/documents/FolderTreeEnhanced';
import { downloadFile } from '@/utils';
import { DocumentsApi, CasesApi } from '@/api';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';
import IntakeChecklist from '@/components/case/IntakeChecklist';
interface DocumentsTabProps {
  caseId: string;
  visaType?: string;
  onLoadingChange?: (isLoading: boolean) => void;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  caseId,
  visaType,
  onLoadingChange,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [previewDocument, setPreviewDocument] =
    useState<DocumentWithStatus | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dirInputRef = useRef<HTMLInputElement | null>(null);
  const [supportsDirPicker, setSupportsDirPicker] = useState(false);

  // Enable directory selection on dedicated hidden input (non-standard attributes)
  useEffect(() => {
    if (dirInputRef.current) {
      dirInputRef.current.setAttribute('webkitdirectory', '');
      dirInputRef.current.setAttribute('directory', '');
    }
    // Feature detect directory picker support (Chrome/Edge/Safari)
    try {
      const probe = document.createElement('input');
      setSupportsDirPicker('webkitdirectory' in (probe as any));
    } catch {
      setSupportsDirPicker(false);
    }
  }, []);

  // Folders - defined before useDocumentManagement so we can pass onBatchComplete callback
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [folders, setFolders] = useState<any[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);

  // Load folders (memoized for use as callback)
  const loadFolders = useCallback(async () => {
    if (!caseId) return;
    setFoldersLoading(true);
    try {
      // Use default filtering: hides empty AI folders, shows manually created empty folders
      const response = await CasesApi.getFolderTree(caseId);
      if (response.success && response.tree) {
        setFolders(response.tree);
      }
    } catch (error) {
      console.error('Failed to load folders:', error);
    } finally {
      setFoldersLoading(false);
    }
  }, [caseId]);

  const {
    documents,
    uploadingDocuments,
    isLoading,
    error,
    criteriaAnalysis,
    uploadDocuments,
    retryDocument,
    deleteDocument,
    renameDocument,
    getDocumentCriteria,
    loadDocuments,
    loadCriteriaAnalysis,
  } = useDocumentManagement({
    caseId,
    pollingInterval: 10000,
    // Refresh folders after batch upload completes (with delay for backend reorganization)
    onBatchComplete: loadFolders,
  });

  // Archive download state (must be declared before any early return to keep hook order stable)
  const [downloadingArchive, setDownloadingArchive] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Highlighted document for scrolling
  const [highlightedDocId, setHighlightedDocId] = useState<string | null>(null);

  // Dismissible banners
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Load up to 500 docs initially (ignoring pagination UI for now)
  // Increased from 100 to support cases with 200+ documents
  useEffect(() => {
    if (caseId) {
      loadDocuments(1, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // Notify parent about loading state
  useEffect(() => {
    const hasActiveUploads = uploadingDocuments.length > 0;
    const hasProcessing = documents.some((d: DocumentWithStatus) =>
      [
        'UPLOADING',
        'PENDING',
        'PROCESSING',
        'AI_CATEGORIZING',
        'OCR_PROCESSING',
      ].includes(d.processingStatus)
    );
    const isActive = hasActiveUploads || hasProcessing;

    if (onLoadingChange) {
      onLoadingChange(isActive);
    }
  }, [uploadingDocuments, documents, onLoadingChange]);

  useEffect(() => {
    if (caseId) {
      loadFolders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // Refresh folders when documents are processed
  const prevProcessingRef = useRef(false);

  useEffect(() => {
    const hasProcessing = documents.some(
      (d: DocumentWithStatus) => d.uploadStatus === 'processing'
    );

    // If processing just finished, reload folders
    if (!hasProcessing && prevProcessingRef.current) {
      loadFolders();
    }

    prevProcessingRef.current = hasProcessing;
  }, [documents, caseId]);

  // Helper function to flatten folder tree
  const flattenFolders = (folderTree: any[]): any[] => {
    const result: any[] = [];
    const traverse = (folders: any[]) => {
      folders.forEach(folder => {
        result.push(folder);
        if (folder.children && folder.children.length > 0) {
          traverse(folder.children);
        }
      });
    };
    traverse(folderTree);
    return result;
  };

  // Filtering by folder
  const filteredDocuments = useMemo(() => {
    let filtered = documents;

    // Apply folder filter if selected
    if (selectedFolder) {
      // Flatten the folder tree to find matching folder by path
      const flatFolders = flattenFolders(folders);
      const matchingFolder = flatFolders.find(f => f.path === selectedFolder);

      if (matchingFolder) {
        filtered = documents.filter((d: DocumentWithStatus) => {
          return d.folderId === matchingFolder.id;
        });
      } else {
        // No matching folder, show no documents
        filtered = [];
      }
    }

    // Sort: uploading/processing first, then completed, then failed
    return filtered.sort((a, b) => {
      const getStatusPriority = (status: string) => {
        if (status === 'UPLOADING') return 0;
        if (
          [
            'PENDING',
            'PROCESSING',
            'AI_CATEGORIZING',
            'OCR_PROCESSING',
          ].includes(status)
        )
          return 1;
        if (status === 'COMPLETED') return 2;
        if (status === 'FAILED') return 3;
        return 4;
      };

      const priorityA = getStatusPriority(a.processingStatus);
      const priorityB = getStatusPriority(b.processingStatus);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // Same priority: sort by upload date (newest first)
      return (
        new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
      );
    });
  }, [documents, selectedFolder, folders]);

  const handlePreview = (document: DocumentWithStatus) => {
    setPreviewDocument(document);
  };

  const handleDocumentClick = async (documentId: string) => {
    // Select "All" folder to show all documents
    setSelectedFolder(null);

    // Wait for next tick to ensure documents are rendered
    await new Promise(resolve => setTimeout(resolve, 100));

    // Find document element and scroll to it
    const element = document.getElementById(`document-${documentId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Highlight the document
      setHighlightedDocId(documentId);

      // Remove highlight after 3 seconds
      setTimeout(() => setHighlightedDocId(null), 3000);
    }
  };

  const handleDownload = async (doc: DocumentWithStatus) => {
    if (!doc.filePath) return;

    try {
      await downloadFile({
        fileName: doc.fileName,
        filePath: doc.filePath,
        fallbackToNewTab: true,
      });
    } catch (error) {
      console.error('Download failed:', error);
      // User will still see the fallback (new tab) from downloadFile
    }
  };

  // NOTE: Keep hooks above; this early return is safe now since no hooks appear below that depend on conditional execution.
  if (isLoading && documents.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const total = documents.length;
  // Treat only actively processing items as processing; FAILED and other terminal states are not processing
  const processingCount = documents.filter(
    d => d.uploadStatus === 'processing'
  ).length;
  const completedCount = documents.filter(
    d => d.processingStatus === 'COMPLETED'
  ).length;
  const failedCount = documents.filter(
    d => d.processingStatus === 'FAILED'
  ).length;
  const allProcessed = total > 0 && processingCount === 0;
  const progressPercent = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  const handleDownloadAll = async () => {
    if (!allProcessed || downloadingArchive) return;
    setDownloadError(null);
    setDownloadingArchive(true);
    try {
      const { blob, filename } = await DocumentsApi.downloadArchive(caseId);
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e: any) {
      console.error('Archive download failed', e);
      setDownloadError(e.message || 'Download failed');
    } finally {
      setDownloadingArchive(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Uploading Progress Banner - Shows during file upload phase */}
      {uploadingDocuments.length > 0 && (
        <Card padding="md" className="bg-indigo-50 border-indigo-200">
          <div className="space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RiUploadLine className="w-5 h-5 text-indigo-600 animate-bounce" />
                <span className="font-medium text-indigo-900">Uploading Files</span>
              </div>
              <span className="text-sm font-semibold text-indigo-700">
                {uploadingDocuments.length} file{uploadingDocuments.length !== 1 ? 's' : ''} uploading...
              </span>
            </div>

            {/* Animated upload indicator */}
            <div className="relative h-2 bg-indigo-200 rounded-full overflow-hidden">
              <div
                className="absolute inset-0 bg-indigo-500 rounded-full"
                style={{
                  animation: 'uploadPulse 1.5s ease-in-out infinite',
                  width: '30%'
                }}
              />
            </div>

            {/* Upload note */}
            <p className="text-xs text-indigo-600">
              Files will be processed automatically after upload completes
            </p>
          </div>
        </Card>
      )}

      {/* Processing Progress Banner - Shows after upload, during document processing */}
      {total > 0 && processingCount > 0 && uploadingDocuments.length === 0 && (
        <Card padding="md" className="bg-blue-50 border-blue-200">
          <div className="space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RiLoader4Line className="w-5 h-5 text-blue-600 animate-spin" />
                <span className="font-medium text-blue-900">Processing Documents</span>
              </div>
              <span className="text-sm font-semibold text-blue-700">
                {completedCount} / {total} processed ({progressPercent}%)
              </span>
            </div>

            {/* Simple Progress Bar */}
            <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
              {/* Processed portion (green) */}
              <div
                className="h-full bg-green-500 transition-all duration-500 ease-out rounded-full"
                style={{ width: `${progressPercent}%` }}
              />
              {/* Subtle shimmer animation on remaining portion */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_2s_infinite]"
                   style={{ transform: 'translateX(-100%)', animation: 'shimmer 2s infinite' }} />
            </div>

            {/* Simple status */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                {total - completedCount - failedCount} remaining
              </span>
              {failedCount > 0 && (
                <span className="text-red-600 font-medium flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {failedCount} failed
                </span>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* All Processed Success Banner */}
      {total > 0 && allProcessed && failedCount === 0 && !bannerDismissed && (
        <Card padding="md" className="bg-green-50 border-green-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-medium text-green-900">All {total} documents processed!</p>
              <p className="text-sm text-green-700">Documents have been sorted into folders.</p>
            </div>
            <button
              type="button"
              onClick={() => setBannerDismissed(true)}
              className="p-1 rounded hover:bg-green-200 text-green-600 hover:text-green-800 transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </Card>
      )}

      {/* Processed with failures banner */}
      {total > 0 && allProcessed && failedCount > 0 && !bannerDismissed && (
        <Card padding="md" className="bg-yellow-50 border-yellow-200">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-yellow-900">{completedCount} processed, {failedCount} failed</p>
              <div className="mt-1 space-y-1">
                {documents
                  .filter(d => d.processingStatus === 'FAILED')
                  .slice(0, 5)
                  .map(doc => (
                    <p key={doc.id} className="text-sm text-yellow-800 truncate flex items-center gap-1">
                      <svg className="w-3 h-3 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="truncate">{doc.customFileName || doc.fileName}</span>
                    </p>
                  ))}
                {failedCount > 5 && (
                  <p className="text-xs text-yellow-600">...and {failedCount - 5} more</p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setBannerDismissed(true)}
              className="p-1 rounded hover:bg-yellow-200 text-yellow-600 hover:text-yellow-800 transition-colors flex-shrink-0"
              aria-label="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </Card>
      )}

      {/* Upload Section - Primary action first */}
      <Card padding="lg">
        <div
          onDragOver={e => {
            e.preventDefault();
            if (!isDragging) setIsDragging(true);
          }}
          onDragLeave={e => {
            if (e.currentTarget.contains(e.relatedTarget as Node)) return;
            setIsDragging(false);
          }}
          onDrop={async e => {
            e.preventDefault();
            setIsDragging(false);
            try {
              const files = Array.from(e.dataTransfer.files);
              if (files.length > 0) {
                await uploadDocuments(files);
              }
            } catch (error) {
              console.error('Upload failed:', error);
            }
          }}
          className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
            isDragging
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-blue-400'
          }`}
        >
          {/* Files input */}
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
            ref={fileInputRef}
            onChange={async e => {
              if (e.target.files && e.target.files.length > 0) {
                const files = Array.from(e.target.files);
                try {
                  await uploadDocuments(files);
                } catch (error) {
                  console.error('Upload failed:', error);
                }
              }
            }}
            className="hidden"
            id="file-upload-files"
          />
          {/* Directory input */}
          <input
            type="file"
            multiple
            // Directory picker (non-standard)
            ref={dirInputRef}
            {...({ webkitdirectory: '' } as any)}
            {...({ directory: '' } as any)}
            onChange={async e => {
              if (e.target.files && e.target.files.length > 0) {
                const files = Array.from(e.target.files);
                try {
                  await uploadDocuments(files);
                } catch (error) {
                  console.error('Upload failed:', error);
                }
              }
            }}
            className="hidden"
            id="file-upload-dir"
          />
          <div className="block">
            <RiUploadLine className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-base font-medium text-gray-900 mb-1">
              Drag & drop files or folders here
            </p>
            <p className="text-xs text-gray-500 mb-3">
              PDF, Word, images and text up to 250MB each
            </p>
            <div className="flex items-center justify-center gap-2 text-[11px]">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-2 py-1 rounded border border-blue-400 text-blue-700 bg-blue-50 hover:bg-blue-100"
              >
                Browse files
              </button>
              <span className="text-gray-400">or</span>
              {supportsDirPicker ? (
                <button
                  type="button"
                  onClick={() => dirInputRef.current?.click()}
                  className="px-2 py-1 rounded border border-blue-400 text-blue-700 bg-blue-50 hover:bg-blue-100"
                >
                  Select folder
                </button>
              ) : (
                <span className="text-gray-400">
                  use drag-and-drop for folders
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Criteria Analysis - Shows processing status and results */}
      <CaseCriteriaAnalysis
        visaType={criteriaAnalysis?.strengthAnalysis?.visaType}
        analysis={criteriaAnalysis?.strengthAnalysis?.criteriaAnalysis}
        documentsCount={documents.length}
        documents={documents.map((d: DocumentWithStatus) => ({
          id: d.id,
          fileName: d.fileName.replace(/_/g, ' '),
          validationResult: d.validationResult,
        }))}
        caseId={caseId}
        onRefresh={async () => {
          await loadDocuments(1, 500);
          await loadCriteriaAnalysis();
        }}
        onDocumentClick={handleDocumentClick}
      />

      {/* Intake Checklist - Progress tracking */}
      <IntakeChecklist caseId={caseId} visaType={visaType || criteriaAnalysis?.strengthAnalysis?.visaType} />

      {/* Folder Tree + Documents Split */}
      <div className="flex flex-col md:flex-row gap-6">
        <div className="md:w-80 md:flex-shrink-0 space-y-3 md:sticky md:top-4 md:self-start min-h-[400px] md:max-h-[calc(100vh-6rem)] md:overflow-y-auto">
          {/* Download all processed button moved here */}
          <div>
            <button
              type="button"
              onClick={handleDownloadAll}
              disabled={!allProcessed || documents.length === 0}
              className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border text-[13px] font-medium transition-colors
                ${allProcessed && documents.length > 0 ? 'border-blue-300 text-blue-600 bg-blue-50 hover:bg-blue-100' : 'border-gray-200 text-gray-400 bg-gray-100 cursor-not-allowed'}
              `}
            >
              {!allProcessed && processingCount > 0 && (
                <RiLoader4Line className="w-4 h-4 animate-spin text-gray-400" />
              )}
              {allProcessed && documents.length > 0 && (
                <RiDownloadCloud2Line className="w-4 h-4 text-blue-600" />
              )}
              <span className="truncate">
                {allProcessed
                  ? downloadingArchive
                    ? 'Preparing archive…'
                    : 'Download processed archive'
                  : processingCount > 0
                    ? `Files processing… (${processingCount})`
                    : 'No files to download'}
              </span>
            </button>
            {downloadError && (
              <div className="mt-1 text-[10px] text-red-600 text-center break-all">
                {downloadError}
              </div>
            )}
          </div>
          {foldersLoading && (
            <div className="space-y-2" aria-label="Loading folders">
              <div className="h-3 w-28 bg-gray-200 rounded animate-pulse" />
              <div className="space-y-1">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-4 w-full bg-gray-100 rounded animate-pulse"
                  />
                ))}
              </div>
            </div>
          )}
          {!foldersLoading && (
            <FolderTreeEnhanced
              caseId={caseId}
              folders={folders}
              onSelect={p => setSelectedFolder(p)}
              selectedPath={selectedFolder}
              onRefresh={() => {
                loadFolders();
                loadDocuments(1, 500);
              }}
            />
          )}
        </div>
        <div className="flex-1 space-y-4 overflow-auto">
          {/* Show helpful message when viewing Unsorted folder */}
          {selectedFolder &&
            filteredDocuments.length > 0 &&
            (() => {
              const flatFolders = flattenFolders(folders);
              const currentFolder = flatFolders.find(
                f => f.path === selectedFolder
              );
              const isUnsortedFolder =
                currentFolder &&
                (currentFolder.name?.toLowerCase() === 'other' ||
                  currentFolder.name?.toLowerCase() === 'uncategorized' ||
                  currentFolder.name?.toLowerCase() === 'unsorted' ||
                  currentFolder.name?.toLowerCase() === 'unrecognized');

              return isUnsortedFolder ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 text-amber-600">
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-amber-900 mb-1">
                        These documents need manual review
                      </h4>
                      <p className="text-sm text-amber-800">
                        AI couldn't categorize these files automatically. You
                        can:
                      </p>
                      <ul className="mt-2 text-sm text-amber-700 list-disc list-inside space-y-1">
                        <li>
                          Drag documents to the correct folder in the tree
                        </li>
                        <li>Use the folder icon menu on each document</li>
                        <li>Create new folders if needed</li>
                      </ul>
                    </div>
                  </div>
                </div>
              ) : null;
            })()}

          {/* Show uploading documents first */}
          {uploadingDocuments.map((document: DocumentWithStatus) => (
            <DocumentItem
              key={document.id}
              document={document}
              criteria={[]}
              caseId={caseId}
              onRetry={retryDocument}
              onDelete={deleteDocument}
              onRename={renameDocument}
              onPreview={() => {}}
              onDownload={() => {}}
              onMoveSuccess={() => {
                loadFolders();
                loadDocuments(1, 500);
              }}
            />
          ))}

          {/* Then show actual documents */}
          {filteredDocuments.length > 0 ? (
            filteredDocuments.map((document: DocumentWithStatus) => (
              <div key={document.id} id={`document-${document.id}`}>
                <DocumentItem
                  document={document}
                  criteria={getDocumentCriteria(document.id)}
                  allCriteria={
                    criteriaAnalysis?.strengthAnalysis?.criteriaAnalysis || []
                  }
                  caseId={caseId}
                  onRetry={retryDocument}
                  onDelete={deleteDocument}
                  onRename={renameDocument}
                  onPreview={handlePreview}
                  onDownload={handleDownload}
                  onMoveSuccess={() => {
                    loadFolders();
                    loadDocuments(1, 500);
                  }}
                  onCriteriaAssignmentSuccess={async () => {
                    await loadCriteriaAnalysis();
                  }}
                  highlighted={highlightedDocId === document.id}
                />
              </div>
            ))
          ) : uploadingDocuments.length === 0 ? (
            <Card className="text-center py-12">
              <RiFileTextLine className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {selectedFolder
                  ? 'No documents in this folder'
                  : 'No documents uploaded yet'}
              </h3>
              {!selectedFolder && (
                <p className="text-gray-500 mb-2">
                  Use the upload area above to add your first documents
                </p>
              )}
            </Card>
          ) : null}
        </div>
      </div>

      {/* Document Preview Modal */}
      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          isOpen={!!previewDocument}
          onClose={() => setPreviewDocument(null)}
          onDownload={handleDownload}
        />
      )}
    </div>
  );
};

export default DocumentsTab;
